import { memo, useCallback, useMemo } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import { FinancialAccount } from "@common/types/financialAccount";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";
import { isImportedAccountInactiveInSource } from "@common/helpers/importedAccountSourceInactivity";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { VStack } from "@design-system/VStack/VStack";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { AccountNumberPickerItem } from "@components/financialAccount/AccountPickerItem";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { MapMigratedDataTableMeta } from "../MapMigratedDataTaskBody";
import styles from "../MapMigratedDataTaskBody.module.scss";

export const FinancialAccountCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ImportedAccount, number | null>) => {
        const importedAccount = row.original;
        const meta = table.options.meta as MapMigratedDataTableMeta | undefined;
        const financialAccounts = useMemo(
            () => meta?.financialAccounts ?? [],
            [meta?.financialAccounts],
        );

        const accountsById = useMemo(
            () =>
                new Map<number, FinancialAccount>(
                    financialAccounts.map((a) => [a.id, a]),
                ),
            [financialAccounts],
        );
        const accountItems = useMemo(
            () =>
                financialAccounts.map((account) => (
                    <AccountNumberPickerItem
                        key={account.id}
                        account={account}
                    />
                )),
            [financialAccounts],
        );

        const selectedAccount = importedAccount.mappedFinancialAccountId
            ? (accountsById.get(importedAccount.mappedFinancialAccountId) ??
              null)
            : null;

        const connection = selectedAccount
            ? getLeadingAccountConnection(selectedAccount)
            : null;

        const isLocked =
            importedAccount.financialAccountMappingFinishedAt != null;
        const isClosed = importedAccount.isClosed;

        const handleSelect = useCallback(
            (financialAccountId: number | null) => {
                meta?.onSelectFinancialAccount({
                    importedAccountId: importedAccount.id,
                    financialAccountId,
                });
            },
            [meta, importedAccount.id],
        );

        const trigger = useMemo(
            () => (
                <span
                    className={classNames({
                        [viewTableStyles.showOnHover]: !selectedAccount,
                        [viewTableStyles.forceShow]: !!selectedAccount,
                    })}
                >
                    <Picker.CellContent
                        addText="Select account"
                        hideAddIcon
                        value={
                            selectedAccount ? (
                                <AccountCard
                                    account={selectedAccount}
                                    connection={connection ?? undefined}
                                    numberInName
                                    showType={false}
                                    iconSize="sm"
                                />
                            ) : undefined
                        }
                        aria-label={selectedAccount?.name ?? undefined}
                    />
                </span>
            ),
            [selectedAccount, connection],
        );

        if (isClosed) {
            return (
                <ViewTableCell>
                    <VStack gap={2}>
                        <span className={styles.closedLabel}>
                            Account closed
                        </span>
                        {isImportedAccountInactiveInSource(importedAccount) && (
                            <span className={styles.autoClosedHint}>
                                Auto-closed: inactive in source
                            </span>
                        )}
                    </VStack>
                </ViewTableCell>
            );
        }

        if (isLocked) {
            return (
                <ViewTableCell>
                    {selectedAccount && (
                        <AccountCard
                            account={selectedAccount}
                            connection={connection ?? undefined}
                            numberInName
                            showType={false}
                            iconSize="sm"
                        />
                    )}
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell>
                <Picker.Select
                    value={selectedAccount?.id ?? null}
                    onValueChange={handleSelect}
                >
                    {trigger}
                    <Picker.Popup>
                        <Picker.List>
                            {financialAccounts.length === 0 ? (
                                <Picker.Empty>
                                    No accounts available
                                </Picker.Empty>
                            ) : (
                                <>
                                    {accountItems}
                                    {selectedAccount && (
                                        <>
                                            <Picker.Separator />
                                            <Picker.Item
                                                onSelect={() =>
                                                    handleSelect(null)
                                                }
                                            >
                                                Deselect account
                                            </Picker.Item>
                                        </>
                                    )}
                                </>
                            )}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    },
);
FinancialAccountCell.displayName = "FinancialAccountCell";
